import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";

export interface TourStep {
  target: string; // data-tour attribute value
  title: string;
  description: string;
  icon: string; // emoji
}

export const TOUR_STEPS: TourStep[] = [
  { target: "dashboard", title: "Dashboard", description: "Tu centro de control en tiempo real. Aquí ves conversaciones activas, métricas y alertas al instante.", icon: "📊" },
  { target: "conversations", title: "Conversaciones", description: "Todas tus conversaciones de WhatsApp, Instagram, Messenger y más — en una sola bandeja unificada.", icon: "💬" },
  { target: "contacts", title: "Contactos", description: "Tu CRM integrado. Todos tus clientes con historial, etiquetas y datos personalizados.", icon: "👥" },
  { target: "campaigns", title: "Campañas", description: "Envía mensajes masivos a tus contactos. Templates aprobados, segmentación y métricas de entrega.", icon: "📣" },
  { target: "automations", title: "Automatizaciones", description: "Crea asistentes IA que responden 24/7, flujos automatizados y reglas inteligentes.", icon: "🤖" },
  { target: "reports", title: "Reportes", description: "Métricas y analíticas de tu operación. Rendimiento de agentes, canales y satisfacción.", icon: "📈" },
  { target: "settings", title: "Configuración", description: "Conecta tus canales, gestiona tu equipo, personaliza etiquetas y configura tu cuenta.", icon: "⚙️" },
];

const STORAGE_KEY_PREFIX = "flow_tour_state";

function getKey(userId?: string) {
  return userId ? `${STORAGE_KEY_PREFIX}_${userId}` : STORAGE_KEY_PREFIX;
}

export function useGuidedTour(enabled = true) {
  const { user } = useAuth();
  const userId = user?.id;

  const [showWelcome, setShowWelcome] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showCompletion, setShowCompletion] = useState(false);

  // Check on mount if tour should show
  useEffect(() => {
    if (!userId || !enabled) return;
    const key = getKey(userId);
    const stored = localStorage.getItem(key);
    if (!stored) {
      // First time — show welcome
      setShowWelcome(true);
    }
  }, [userId, enabled]);

  const startTour = useCallback(() => {
    setShowWelcome(false);
    setCurrentStep(0);
    setTourActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      // Tour complete
      setTourActive(false);
      setShowCompletion(true);
      if (userId) {
        localStorage.setItem(getKey(userId), JSON.stringify({ completed: true, at: new Date().toISOString() }));
      }
    }
  }, [currentStep, userId]);

  const skipTour = useCallback(() => {
    setShowWelcome(false);
    setTourActive(false);
    setShowCompletion(false);
    if (userId) {
      localStorage.setItem(getKey(userId), JSON.stringify({ completed: true, skipped: true, at: new Date().toISOString() }));
    }
  }, [userId]);

  const dismissCompletion = useCallback(() => {
    setShowCompletion(false);
  }, []);

  return {
    showWelcome: enabled && showWelcome,
    tourActive: enabled && tourActive,
    currentStep,
    showCompletion: enabled && showCompletion,
    totalSteps: TOUR_STEPS.length,
    currentTourStep: TOUR_STEPS[currentStep] || null,
    startTour,
    nextStep,
    skipTour,
    dismissCompletion,
  };
}
